import React from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Context } from "../App";
import { useNavigate } from "react-router-dom";
import { deleteModel, listMyModels, updateModel } from "../services/modelService";
import ModelCardGrid from "../components/models/ModelCardGrid";
import Header from "../components/layout/Header";

function ModelManager() {
    const { deconnect } = React.useContext(Context);
    const navigate = useNavigate();
    const [models, setModels] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");

    const loadModels = React.useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await listMyModels();
            setModels(data);
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return;
            }
            console.error("Erreur lors du chargement des modèles:", e);
            setError("Erreur lors du chargement des modèles.");
        } finally {
            setLoading(false);
        }
    }, [deconnect]);

    const handleDelete = async (model) => {
        const ok = window.confirm(`Supprimer le modèle "${model.title}" ?`);
        if (!ok) return;

        try {
            await deleteModel(model._id);
            setModels((prev) => prev.filter((m) => m._id !== model._id));
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return;
            }
            alert("Erreur lors de la suppression du modèle.");
        }
    };

    const handleEdit = (model) => navigate(`/model/${model._id}/edit`);
    const handlePreview = (model) => navigate(`/model/${model._id}/view`);
    const handleCreateDesign = (model) => navigate("/design/new", { state: { modelId: model._id } });

    const handleRename = async (model) => {
        const nextTitle = window.prompt("Nouveau nom du modèle", model.title || "");
        if (!nextTitle) return;
        const trimmed = nextTitle.trim();
        if (!trimmed || trimmed === model.title) return;

        try {
            const updated = await updateModel(model._id, { ...model, title: trimmed });
            setModels((prev) => prev.map((m) => (m._id === model._id ? updated : m)));
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return;
            }
            alert("Erreur lors du renommage du modèle.");
        }
    };

    React.useEffect(() => {
        loadModels();
    }, [loadModels]);

    return (
        <div>
            <Header />
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Model Manager
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Créez des modèles templates (champs positionnés et stylés), puis générez des designs via formulaire sans ouvrir l'éditeur.
                </Typography>

                <Stack direction="row" spacing={1.5}>
                    <Button variant="contained" href="/model/new">Nouveau modèle</Button>
                    <Button variant="outlined" href="/design/new">Créer un design depuis un modèle</Button>
                    <Button variant="text" onClick={loadModels}>Actualiser</Button>
                </Stack>

                <Box sx={{ mt: 2 }}>
                    {loading && (
                        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!loading && error && <Alert severity="error">{error}</Alert>}
                    {!loading && !error && (
                        <ModelCardGrid
                            models={models}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onPreview={handlePreview}
                            onCreateDesign={handleCreateDesign}
                            onRename={handleRename}
                        />
                    )}
                </Box>
            </Box>
        </div>
    );
}

export default ModelManager;
